import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./AccessoriesMetals.css";
import { useTranslation } from "react-i18next";
import { getAccessories } from "../../../../api/metals";
import { resolveMediaUrl } from "../../../../utils/media";
import { formatUnitSuperscriptText } from "../../../../utils/unitSuperscriptText";
import { filterPublishedProducts } from "../../../../utils/publicProducts";

import metal1 from "../../../../assets/images/accessories/metal1.png";
import metal2 from "../../../../assets/images/accessories/metal2.png";
import metal3 from "../../../../assets/images/accessories/metal3.png";

const FALLBACK_IMAGE_BY_KEY = {
  "casing-bead": metal1,
  "ceiling-metal-frame": metal2,
  "deflection-fire-track": metal3,
};
const getFallbackImageForAccessory = ({ accessoryKey, name, index }) => {
  const key = String(accessoryKey || "")
    .trim()
    .toLowerCase();
  if (key && FALLBACK_IMAGE_BY_KEY[key]) return FALLBACK_IMAGE_BY_KEY[key];

  const normalizedName = String(name || "")
    .trim()
    .toLowerCase();
  if (normalizedName.includes("casing bead")) return metal1;
  if (normalizedName.includes("ceiling metal frame")) return metal2;
  if (normalizedName.includes("deflection fire track")) return metal3;

  const byIndex = [metal1, metal2, metal3];
  return byIndex[index % byIndex.length];
};

const ACCESSORIES_DISPLAY_ORDER = [
  "Corner Tape",
  "Fiberglass Mesh Tape",
  "Paper Joint Tape",
  "Fiberglass",
  "Rockwool",
  "Sealant",
  "Casing Bead",
  "Cross Connector for CD60",
  "Drywall Control Joint",
  "Flexible Corner Bead",
  "Furring Channel Joiner",
  "Perforated Angle Bead",
  "Perforated Angle Bead X perforation",
  "Z-shadowline",
  "Wire Hanger",
  "Anchor Bolt With sleeve",
  "Anchor Bolt Without Sleeve",
  "Drop in Anchor",
  "Nylon Wall Plug",
  "Self Drilling Screw",
  "Self-Tapping Screw",
  "Wafer Head Screw",
];

const normalizeAccessoryTitle = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const ACCESSORY_ORDER_MAP = ACCESSORIES_DISPLAY_ORDER.reduce((acc, title, index) => {
  acc[normalizeAccessoryTitle(title)] = index;
  return acc;
}, {});

const AccessoriesMetals = () => {
  const { i18n } = useTranslation();
  const [apiItems, setApiItems] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const data = await getAccessories({ language: String(i18n.language || "en").toLowerCase() });
        if (isMounted) {
          setApiItems(filterPublishedProducts(data));
        }
      } catch (_error) {
        if (isMounted) {
          setApiItems([]);
        }
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  const items = useMemo(() => {
    if (!Array.isArray(apiItems) || !apiItems.length) return [];

    const mapped = apiItems
      .map((item, index) => {
        const key = item?.accessoryKey || item?.id;
        if (!key) return null;
        const resolvedImage = resolveMediaUrl(item?.imageUrl);
        const fallbackImage = getFallbackImageForAccessory({
          accessoryKey: item?.accessoryKey,
          name: item?.name || item?.heroTitle,
          index,
        });
        return {
          id: String(key),
          title: item?.name || item?.heroTitle || "Metal Accessory",
          description:
            item?.heroDescription ||
            "Metal accessory details are available in the product page.",
          image: resolvedImage || fallbackImage,
        };
      })
      .filter(Boolean);

    if (!mapped.length) return [];

    return [...mapped].sort((a, b) => {
      const aOrder = ACCESSORY_ORDER_MAP[normalizeAccessoryTitle(a?.title)];
      const bOrder = ACCESSORY_ORDER_MAP[normalizeAccessoryTitle(b?.title)];
      const aRank = Number.isFinite(aOrder) ? aOrder : Number.MAX_SAFE_INTEGER;
      const bRank = Number.isFinite(bOrder) ? bOrder : Number.MAX_SAFE_INTEGER;
      if (aRank !== bRank) return aRank - bRank;
      return String(a?.title || "").localeCompare(String(b?.title || ""));
    });
  }, [apiItems]);

  return (
    <section className="accessoriesMetals">
      <div className="accessoriesMetals-grid">
        {items.map((item) => (
          <Link
            key={item.id}
            to={`/products/accessories/${item.id}`}
            className="accessoriesMetals-card"
          >
            <div className="accessoriesMetals-imageWrap">
              <img src={item.image} alt={item.title} />
            </div>

            <div className="accessoriesMetals-content">
              <h3>{formatUnitSuperscriptText(item.title)}</h3>
              <p>{formatUnitSuperscriptText(item.description)}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default AccessoriesMetals;